import { useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { isAtRoute } from 'router/routeMatch';
import { useBrandId } from 'AppProvider';
import useEventBookingSessionPropsFromUrl from './useEventBookingSessionPropsFromUrl';
import useEventBookingSessionSelectedData from './useEventBookingSessionSelectedData';

interface BookingProps {
  eventId?: string;
  date?: string;
  preselectedDate?: string;
  latest?: boolean;
  utm?: string;
}

const formatNextBookingUrl = (props: BookingProps) => {
  let url = '';

  if (props.eventId) {
    url += `/event/${props.eventId}`;

    const searchParams = new URLSearchParams();
    if (props.date) {
      url += `/tickets/${props.date}`;
    }

    if (props.preselectedDate) {
      searchParams.set('preselectedDate', props.preselectedDate);
    }

    if (props.latest) {
      searchParams.set('latest', 'true');
    }

    if (props.utm) {
      searchParams.set('utm', props.utm);
    }

    if (searchParams.size) {
      url += `?${searchParams.toString()}`;
    }
  }

  return url;
};

const useEventBookingSession = () => {
  const location = useLocation();
  const history = useHistory();
  const brandId = useBrandId();

  /* eslint-disable prefer-const */
  // 1 - get props from url
  let { eventId, date, preselectedDate, latest, utm } =
    useEventBookingSessionPropsFromUrl();

  // 2 - get data using props from url
  let {
    isLoading,
    isLoadingListing,
    isLoadingEventSessions,
    isLoadingDaySessions,
    isLoadingCancellationPolicy,
    hasExchangeIdLoaded,
    listingId,
    exchangeIdError,
    listingError,
    listing,
    eventSessions,
    daySessions,
    cancellationPolicy,
  } = useEventBookingSessionSelectedData({
    brandId,
    eventId,
    date,
    latest,
  });
  /* eslint-enable prefer-const */

  // 3 - if the given ids are not found, reset the data
  if (!listing) {
    eventId = undefined;
    date = undefined;
    eventSessions = [];
    daySessions = [];
  }

  // Once the listing resolves, prefer its slug for the event segment so the URL the page normalizes
  // to (via cleanedUrl) and every param change stay keyword-rich. This is the UUID→slug swap for a
  // UUID (or missed-slug) entry. Falls back to the raw route value when the listing has no slug yet.
  const eventRouteId = listing?.slug ?? eventId;

  // 4 - format new cleaned url
  const cleanedUrl = formatNextBookingUrl({
    eventId: eventRouteId,
    date,
    preselectedDate,
    latest,
    utm,
  });

  const setEventBookingSessionParams = useCallback(
    (changes: BookingProps, forceHistoryPush = false) => {
      const nextUrl = formatNextBookingUrl({
        eventId: eventRouteId,
        date,
        preselectedDate,
        latest,
        utm,
        ...changes,
      });

      if (!isAtRoute(location, nextUrl)) {
        const [pathname, search] = nextUrl.split('?');
        if (forceHistoryPush || location.pathname !== pathname) {
          history.push({
            pathname,
            search,
          });
        } else {
          history.replace({
            pathname,
            search,
          });
        }
      }
    },
    [location, history, eventRouteId, date, preselectedDate, latest, utm],
  );

  return {
    isLoading,
    isLoadingListing,
    isLoadingEventSessions,
    isLoadingDaySessions,
    isLoadingCancellationPolicy,
    hasExchangeIdLoaded,
    listingId,
    exchangeIdError,
    listingError,
    cleanedUrl,
    listing,
    eventSessions,
    daySessions,
    cancellationPolicy,
    cancellationPolicyEnabled:
      !!cancellationPolicy && cancellationPolicy.active,
    eventId,
    date,
    preselectedDate,
    latest,
    setEventBookingSessionParams,
  };
};

export default useEventBookingSession;
